import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import supabase from "../config/supabaseConfig";
import "../styles/CargasAluno.css";

// Editar Cargas (patch 2026) — layout de docs/prototipos/cargas.html.
// Tela SEPARADA do cadastro (reunião 12/06): "Editar Cadastro" ≠ "Editar
// Cargas" — aqui é só controle de treino (carga máxima + volume por
// grupamento).
// Persistência (Task A2, 10/07): carga máxima grava em `aluno_cargas`
// (upsert por aluno_id+exercicio_id — UNIQUE criada na A1). Volume por
// grupamento (abaixo) grava a META em `aluno_volume.meta` (upsert por
// aluno_id+grupamento) — só as 10 siglas fixas, CHECK do banco não aceita
// outra coisa. `series_semana` (apontamento acumulado da Sala) não é tocado
// aqui. Grupamento extra criado nessa tela ainda não persiste (14/07).

// 8 exercícios de carga validados (§ regras): 7 fixos + "outro" (extra).
// IDs fixos em `exercicios` (seed idempotente em
// scripts/migrations/2026-07-10_seed_exercicios_carga.sql) — nenhuma das 10
// linhas pré-existentes da tabela batia com esses rótulos (catálogo
// diferente, usado por aula_exercicios), então cada rótulo da tela ganhou
// linha própria com UUID fixo. Hardcode aqui evita depender de SELECT em
// `exercicios` em runtime (RLS da tabela só libera leitura pra
// admin/professor, não pra lider/recepção).
export const EXERCICIOS_FIXOS = [
  { nome: "Supino", exercicioId: "14925579-3aa2-4a78-aabf-9a8d172a353d" },
  { nome: "Agachamento", exercicioId: "ad6033c0-66a5-48ea-aa20-38959fdc89bb" },
  {
    nome: "Levantamento terra",
    exercicioId: "3c9ac90a-3f2d-4147-8a74-36821184ff65",
  },
  {
    nome: "Cadeira extensora",
    exercicioId: "57dbb00e-aaae-4645-9ffc-3ab45227f1cb",
  },
  { nome: "Mesa flexora", exercicioId: "934d6110-9040-4cd5-8d9f-6751d4940e25" },
  {
    nome: "Puxada frontal",
    exercicioId: "3eaa921c-3434-431a-8b94-651e1eae52f7",
  },
  { nome: "Remada", exercicioId: "8dd4ff5c-f72e-4f27-9e5d-a6881f347253" },
];

// % de carga (validado em reunião 12/06): alta 90%, média 75%, baixa 65%.
export const PCT = { alta: 0.9, media: 0.75, baixa: 0.65 };
export const calcPct = (max, pct) => (max ? Math.round(max * pct) : null);
// Caminho inverso (pedido Tales 02/10): preencher Alta/Média/Baixa também
// preenche a máxima (e por tabela as outras faixas). Máxima com 1 casa
// decimal (carga_maxima é numeric): o erro de arredondamento fica < 0,5 kg,
// então a faixa digitada (inteira) volta idêntica pelo calcPct.
export const maxDePct = (valor, pct) => {
  const v = Number(valor);
  return v > 0 ? Math.round((v / pct) * 10) / 10 : "";
};

// 10 grupamentos musculares validados (Roadmap V2). Séries-chave default
// realista (ex. do Alex na reunião: quadríceps 6, posterior 10).
const GRUPAMENTOS_BASE = [
  { sigla: "QD", nome: "Quadríceps", series: 6 },
  { sigla: "PC", nome: "Posterior de coxa", series: 10 },
  { sigla: "PT", nome: "Panturrilha", series: 6 },
  { sigla: "A", nome: "Adutores", series: 6 },
  { sigla: "G", nome: "Glúteo", series: 6 },
  { sigla: "P", nome: "Peitoral", series: 6 },
  { sigla: "D", nome: "Dorsal", series: 6 },
  { sigla: "O", nome: "Ombro", series: 6 },
  { sigla: "B", nome: "Bíceps", series: 6 },
  { sigla: "T", nome: "Tríceps", series: 6 },
];

let extraIdSeq = 0;

const CargasAluno = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // returnTo (feedback Tales 08/07): voltar respeita a origem (full screen
  // ou Sala); ?from=sala já vinha do EditarAluno mas era ignorado aqui —
  // era ESSE o "cai no cadastro" da reclamação. Sem params, regra antiga.
  const returnTo = searchParams.get("returnTo");
  const origemSala = searchParams.get("from") === "sala";

  const [aluno, setAluno] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [salvo, setSalvo] = useState(false);

  const [exercicios, setExercicios] = useState(
    EXERCICIOS_FIXOS.map(({ nome, exercicioId }) => ({
      id: nome,
      exercicioId,
      nome,
      reps: "",
      max: "",
      isFixo: true,
    }))
  );
  const [novoExtra, setNovoExtra] = useState("");
  const [grupamentos, setGrupamentos] = useState(GRUPAMENTOS_BASE);
  const [novoGrupamento, setNovoGrupamento] = useState("");
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        setCarregando(true);
        const { data, error } = await supabase
          .from("alunos")
          .select("id, nome, plano")
          .eq("id", id)
          .single();
        if (error) throw error;
        if (ativo) setAluno(data);

        // Carga (Task A2): busca aluno_cargas + o nome do exercício (pra
        // reconstituir extras) via join simples.
        const { data: cargas, error: errCargas } = await supabase
          .from("aluno_cargas")
          .select("exercicio_id, carga_maxima, is_fixo, exercicios(nome)")
          .eq("aluno_id", id);
        if (errCargas) throw errCargas;

        if (ativo && cargas?.length) {
          setExercicios((prev) => {
            const porExercicioId = new Map(
              cargas.map((c) => [c.exercicio_id, c])
            );
            // Preenche os 7 fixos com a carga salva (se houver).
            const atualizados = prev.map((e) => {
              const c = porExercicioId.get(e.exercicioId);
              if (!c) return e;
              porExercicioId.delete(e.exercicioId);
              return { ...e, max: c.carga_maxima ?? "" };
            });
            // O que sobrar em porExercicioId são extras (is_fixo=false)
            // salvos em sessões anteriores — reconstitui as linhas.
            const extras = Array.from(porExercicioId.values())
              .filter((c) => !c.is_fixo)
              .map((c) => {
                extraIdSeq += 1;
                return {
                  id: `extra-${extraIdSeq}`,
                  exercicioId: c.exercicio_id,
                  nome: c.exercicios?.nome || "Exercício extra",
                  reps: "",
                  max: c.carga_maxima ?? "",
                  extra: true,
                };
              });
            return [...atualizados, ...extras];
          });
        }

        // Volume (grupamentos): busca a meta salva e aplica por cima do
        // default de GRUPAMENTOS_BASE (match por sigla; meta null mantém o
        // default — evita sobrescrever com "vazio" quando a linha existe
        // mas meta não foi preenchida).
        const { data: volumes, error: errVolumes } = await supabase
          .from("aluno_volume")
          .select("grupamento, meta")
          .eq("aluno_id", id);
        if (errVolumes) throw errVolumes;

        if (ativo && volumes?.length) {
          const metaPorSigla = new Map(
            volumes
              .filter((v) => v.meta != null)
              .map((v) => [v.grupamento, v.meta])
          );
          if (metaPorSigla.size) {
            setGrupamentos((prev) =>
              prev.map((g) =>
                metaPorSigla.has(g.sigla)
                  ? { ...g, series: metaPorSigla.get(g.sigla) }
                  : g
              )
            );
          }
        }
      } catch (err) {
        console.error("Erro ao carregar aluno/cargas:", err);
        if (ativo) setErro("Erro ao carregar aluno: " + err.message);
      } finally {
        if (ativo) setCarregando(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [id]);

  const setExercicioCampo = (exId, campo, valor) => {
    setExercicios((prev) =>
      prev.map((e) => (e.id === exId ? { ...e, [campo]: valor } : e))
    );
  };

  const adicionarExtra = () => {
    const nome = novoExtra.trim();
    if (!nome) return;
    extraIdSeq += 1;
    setExercicios((prev) => [
      ...prev,
      { id: `extra-${extraIdSeq}`, nome, reps: "", max: "", extra: true },
    ]);
    setNovoExtra("");
  };

  const removerExtra = (exId) => {
    setExercicios((prev) => prev.filter((e) => e.id !== exId));
  };

  const setSeriesGrupamento = (sigla, valor) => {
    setGrupamentos((prev) =>
      prev.map((g) => (g.sigla === sigla ? { ...g, series: valor } : g))
    );
  };

  const adicionarGrupamento = () => {
    const nome = novoGrupamento.trim();
    if (!nome) return;
    const sigla = nome.slice(0, 4).toUpperCase();
    setGrupamentos((prev) => [
      ...prev,
      { sigla, nome, series: 6, extra: true },
    ]);
    setNovoGrupamento("");
  };

  const removerGrupamento = (sigla) => {
    setGrupamentos((prev) => prev.filter((g) => g.sigla !== sigla));
  };

  const voltar = () =>
    navigate(returnTo || (origemSala ? "/sala" : `/detalhe-aluno/${id}`));

  const salvar = async () => {
    setSalvando(true);
    setErro(null);
    try {
      const agora = new Date().toISOString();
      // Só grava linhas com carga preenchida (>0) — evita lixo de
      // aluno_cargas pra exercício que o professor nem chegou a lançar.
      const comCarga = exercicios.filter((e) => Number(e.max) > 0);

      // Extras ainda sem exercicio_id (nome digitado agora): resolve/insere
      // em `exercicios` antes do upsert de aluno_cargas. RLS de `exercicios`
      // só libera INSERT/SELECT pra admin/professor — se o papel logado não
      // tiver permissão, o extra é ignorado (fixos continuam salvando).
      const linhas = [];
      const extrasIgnorados = [];
      const idResolvidoPorLinha = new Map(); // e.id (estado local) -> exercicio_id

      for (const e of comCarga) {
        let exercicioId = e.exercicioId;
        if (!exercicioId) {
          try {
            const { data: existente, error: errBusca } = await supabase
              .from("exercicios")
              .select("id")
              .eq("nome", e.nome)
              .maybeSingle();
            if (errBusca) throw errBusca;
            if (existente) {
              exercicioId = existente.id;
            } else {
              const { data: criado, error: errCriar } = await supabase
                .from("exercicios")
                .insert({ nome: e.nome })
                .select("id")
                .single();
              if (errCriar) throw errCriar;
              exercicioId = criado.id;
            }
          } catch (errExtra) {
            console.error("Erro ao resolver exercício extra:", e.nome, errExtra);
            extrasIgnorados.push(e.nome);
            continue;
          }
        }
        idResolvidoPorLinha.set(e.id, exercicioId);
        linhas.push({
          aluno_id: id,
          exercicio_id: exercicioId,
          carga_maxima: Number(e.max),
          is_fixo: !e.extra,
          updated_at: agora,
        });
      }

      if (linhas.length) {
        const { error: errUpsert } = await supabase
          .from("aluno_cargas")
          .upsert(linhas, { onConflict: "aluno_id,exercicio_id" });
        if (errUpsert) throw errUpsert;
      }

      // Guarda o exercicio_id resolvido no estado (evita re-lookup/duplicar
      // insert em `exercicios` no próximo salvar da mesma sessão).
      if (idResolvidoPorLinha.size) {
        setExercicios((prev) =>
          prev.map((e) =>
            idResolvidoPorLinha.has(e.id)
              ? { ...e, exercicioId: idResolvidoPorLinha.get(e.id) }
              : e
          )
        );
      }

      // Volume (grupamentos): upsert só das 10 siglas fixas — o CHECK de
      // `aluno_volume.grupamento` rejeita sigla extra, e grupamento extra
      // (adicionado nessa tela) ainda não tem onde persistir. Payload só
      // com { aluno_id, grupamento, meta } de propósito: o upsert do
      // PostgREST só toca as colunas enviadas, então `series_semana`
      // (apontamento acumulado da Sala) fica intacto.
      const siglasFixas = new Set(GRUPAMENTOS_BASE.map((g) => g.sigla));
      const linhasVolume = grupamentos
        .filter((g) => siglasFixas.has(g.sigla))
        .map((g) => ({
          aluno_id: id,
          grupamento: g.sigla,
          meta: Number(g.series) || 0,
        }));

      if (linhasVolume.length) {
        const { error: errVolume } = await supabase
          .from("aluno_volume")
          .upsert(linhasVolume, { onConflict: "aluno_id,grupamento" });
        if (errVolume) throw errVolume;
      }

      if (extrasIgnorados.length) {
        setErro(
          `Não foi possível salvar o(s) exercício(s) extra(s): ${extrasIgnorados.join(
            ", "
          )} (sem permissão pra criar exercício novo com o papel atual).`
        );
      }
      setSalvo(true);
      setTimeout(() => setSalvo(false), 2500);
    } catch (err) {
      console.error("Erro ao salvar cargas:", err);
      setErro("Erro ao salvar cargas: " + err.message);
    } finally {
      setSalvando(false);
    }
  };

  const linhasCalculadas = useMemo(
    () =>
      exercicios.map((e) => {
        const max = Number(e.max) || 0;
        return {
          ...e,
          alta: calcPct(max, PCT.alta),
          media: calcPct(max, PCT.media),
          baixa: calcPct(max, PCT.baixa),
        };
      }),
    [exercicios]
  );

  if (carregando) {
    return (
      <div className="cargas-aluno">
        <div className="cg-header">
          <h1>Editar Cargas</h1>
        </div>
        <p className="cg-note">Carregando aluno…</p>
      </div>
    );
  }

  return (
    <div className="cargas-aluno">
      <div className="cg-topbar">
        <div className="cg-header">
          <h1>Editar Cargas</h1>
          <span className="cg-sub">
            {aluno?.nome || "—"}
            {aluno?.plano && <span className="cg-badge-plano">{aluno.plano}</span>}
          </span>
        </div>
        <button type="button" className="cg-btn-voltar" onClick={voltar}>
          ← Voltar ao aluno
        </button>
      </div>

      {erro && <div className="cg-erro">{erro}</div>}
      {salvo && (
        <div className="cg-erro cg-sucesso">Cargas salvas.</div>
      )}

      <div className="cg-card">
        <div className="cg-card-top">
          <div className="cg-section-title">Controle de carga</div>
          <div className="cg-legend">
            <span>
              Lança <strong>qualquer faixa</strong> → o sistema calcula as demais:
            </span>
            <span className="cg-legend-tag">Alta 90%</span>
            <span className="cg-legend-tag">Média 75%</span>
            <span className="cg-legend-tag">Baixa 65%</span>
          </div>
        </div>

        <div className="cg-table-scroll">
          <table className="cg-table">
            <thead>
              <tr>
                <th>Exercício</th>
                <th>Reps / obs.</th>
                <th>
                  Máxima <small>100%</small>
                </th>
                <th>
                  Alta <small>90%</small>
                </th>
                <th>
                  Média <small>75%</small>
                </th>
                <th>
                  Baixa <small>65%</small>
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {linhasCalculadas.map((e) => (
                <tr key={e.id}>
                  <td className="cg-ex-nome">{e.nome}</td>
                  <td>
                    <input
                      type="text"
                      placeholder="ex.: 8 reps"
                      value={e.reps}
                      onChange={(ev) =>
                        setExercicioCampo(e.id, "reps", ev.target.value)
                      }
                    />
                  </td>
                  <td>
                    <div className="cg-carga-cell">
                      <input
                        type="number"
                        min="0"
                        placeholder="0"
                        value={e.max}
                        onChange={(ev) =>
                          setExercicioCampo(e.id, "max", ev.target.value)
                        }
                      />
                      <span className="cg-unidade">kg</span>
                    </div>
                  </td>
                  <td>
                    <div className="cg-carga-cell">
                      <input
                        type="number"
                        min="0"
                        placeholder="—"
                        value={e.alta ?? ""}
                        onChange={(ev) =>
                          setExercicioCampo(
                            e.id,
                            "max",
                            maxDePct(ev.target.value, PCT.alta)
                          )
                        }
                      />
                      <span className="cg-unidade">kg</span>
                    </div>
                  </td>
                  <td>
                    <div className="cg-carga-cell">
                      <input
                        type="number"
                        min="0"
                        placeholder="—"
                        value={e.media ?? ""}
                        onChange={(ev) =>
                          setExercicioCampo(
                            e.id,
                            "max",
                            maxDePct(ev.target.value, PCT.media)
                          )
                        }
                      />
                      <span className="cg-unidade">kg</span>
                    </div>
                  </td>
                  <td>
                    <div className="cg-carga-cell">
                      <input
                        type="number"
                        min="0"
                        placeholder="—"
                        value={e.baixa ?? ""}
                        onChange={(ev) =>
                          setExercicioCampo(
                            e.id,
                            "max",
                            maxDePct(ev.target.value, PCT.baixa)
                          )
                        }
                      />
                      <span className="cg-unidade">kg</span>
                    </div>
                  </td>
                  <td>
                    {e.extra && (
                      <button
                        className="cg-x"
                        onClick={() => removerExtra(e.id)}
                        aria-label="Remover exercício"
                      >
                        ×
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="cg-add-row">
          <input
            type="text"
            placeholder="Nome do exercício extra"
            value={novoExtra}
            onChange={(e) => setNovoExtra(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && adicionarExtra()}
          />
          <button type="button" className="cg-btn-add" onClick={adicionarExtra}>
            + Adicionar exercício extra
          </button>
        </div>
      </div>

      <div className="cg-card cg-mt">
        <div className="cg-section-title">Grupamentos &amp; volume (séries)</div>
        <p className="cg-note">
          Número-chave de séries por grupamento. Na Sala, o apontamento fica{" "}
          <strong className="cg-c-leve">amarelo</strong> abaixo,{" "}
          <strong className="cg-c-pontual">verde</strong> no ideal e{" "}
          <strong className="cg-c-sig">vermelho</strong> acima.
        </p>

        <div className="cg-grupos">
          {grupamentos.map((g) => (
            <div key={g.sigla} className="cg-grupo">
              <span className="cg-grupo-sigla">{g.sigla}</span>
              <span className="cg-grupo-nome">{g.nome}</span>
              <span className="cg-grupo-tag">séries-chave</span>
              <input
                type="number"
                min="0"
                className="cg-grupo-input"
                value={g.series}
                onChange={(e) => setSeriesGrupamento(g.sigla, e.target.value)}
              />
              {g.extra && (
                <button
                  className="cg-x"
                  onClick={() => removerGrupamento(g.sigla)}
                  aria-label="Remover grupamento"
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="cg-add-row">
          <input
            type="text"
            placeholder="Outro grupamento"
            value={novoGrupamento}
            onChange={(e) => setNovoGrupamento(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && adicionarGrupamento()}
          />
          <button
            type="button"
            className="cg-btn-add"
            onClick={adicionarGrupamento}
          >
            + Adicionar
          </button>
        </div>
        {grupamentos.some((g) => g.extra) && (
          // CHECK de `aluno_volume.grupamento` só aceita as 10 siglas fixas
          // — grupamento extra fica só na tela (não sobrevive ao reload).
          <p className="cg-note">
            Grupamento extra ainda não é salvo — só as 10 siglas fixas
            persistem.
          </p>
        )}
      </div>

      <div className="cg-actions">
        <button type="button" className="cg-btn-ghost" onClick={voltar}>
          Cancelar
        </button>
        <button
          type="button"
          className="cg-btn-primary"
          onClick={salvar}
          disabled={salvando}
        >
          {salvando ? "Salvando…" : "Salvar cargas"}
        </button>
      </div>
    </div>
  );
};

export default CargasAluno;
